/**
 * Pulls the user-api error code out of a thrown api error.
 *
 * The api layer (`apiCall`) throws the parsed error body on a non-2xx response.
 * user-api serializes errors as `{ type, error: { code }, errors: [{ code }] }`
 * (see `ApiError.getErrorJson`), so the code lives in a few equivalent places —
 * we read them defensively. Returns undefined for network/parse failures where
 * there is no structured body.
 */
export const extractApiErrorCode = (error: unknown): string | undefined => {
    if (!error || typeof error !== 'object') {
        return undefined
    }

    const body = error as {
        type?: string
        error?: { code?: string }
        errors?: { code?: string }[]
    }

    return body.error?.code ?? body.type ?? body.errors?.[0]?.code
}

/**
 * Pulls the structured payload some error codes carry (see `errorPayloads` in
 * user-api). Only the modern `error` shape has it, since the legacy `errors[]`
 * array only ever held a code and a message.
 */
export const extractApiErrorPayload = (error: unknown): unknown => {
    if (!error || typeof error !== 'object') {
        return undefined
    }

    return (error as { error?: { payload?: unknown } }).error?.payload
}
